import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import MainTitle from '../common/MainTitle';
import Modal from '../common/Modal';
import BackupReportsService from './BackupReportsService';
import './BackupReports.css';

const BackupReports = () => {
    const [reports, setReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);
    const [showLogModal, setShowLogModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [total, setTotal] = useState(0);
    const [filterKey, setFilterKey] = useState('');
    const { key } = useParams();

    const loadReports = useCallback(() => {
        BackupReportsService.get(currentPage, pageSize, filterKey || null).then(
            (response) => {
                setReports(response.reports || []);
                setTotal(response.total || 0);
            }
        ).catch((error) => {
            console.error('Error loading backup reports:', error);
        });
    }, [currentPage, pageSize, filterKey]);

    useEffect(() => {
        if (key) {
            setFilterKey(key);
        }
    }, [key]);

    useEffect(() => {
        loadReports();
    }, [loadReports]);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        try {
            return format(parseISO(dateString), 'dd.MM.yyyy HH:mm:ss');
        } catch (e) {
            return dateString;
        }
    };

    const getStatusBadge = (status) => {
        if (status === true || status === 'success') {
            // Support both boolean and legacy string format for backward compatibility
            return <span className="badge bg-success">Успешно</span>;
        } else {
            return <span className="badge bg-danger">Ошибка</span>;
        }
    };

    const showLogs = (report) => {
        setSelectedReport(report);
        setShowLogModal(true);
    };

    const totalPages = Math.ceil(total / pageSize);

    return (
        <div>
            <MainTitle title="Отчеты о резервном копировании" />
            <div className="container-fluid backup-reports">
            <div className="mb-3">
                <div className="row">
                    <div className="col-md-4">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Фильтр по ключу..."
                            value={filterKey}
                            onChange={(e) => {
                                setFilterKey(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-striped table-hover">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Ресурс</th>
                            <th>Дата/Время</th>
                            <th>Статус</th>
                            <th>Файлов</th>
                            <th>Размер</th>
                            <th>Базы данных</th>
                            <th>Директории</th>
                            <th>Media storage</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {reports.length === 0 ? (
                            <tr>
                                <td colSpan="10" className="text-center">
                                    Нет отчетов
                                </td>
                            </tr>
                        ) : (
                            reports.map((report) => {
                                const totalSize = report.files?.reduce((sum, file) => sum + (file.size || 0), 0) || 0;
                                return (
                                    <tr key={report.id}>
                                        <td>{report.id}</td>
                                        <td>
                                            {report.resource_name || (
                                                <code className="text-muted">{report.key}</code>
                                            )}
                                        </td>
                                        <td>{formatDate(report.timestamp)}</td>
                                        <td>{getStatusBadge(report.status)}</td>
                                        <td>{report.files?.length || 0}</td>
                                        <td>{formatFileSize(totalSize)}</td>
                                        <td>
                                            {report.databases_backed_up?.length > 0 ? (
                                                <span className="badge bg-info">
                                                    {report.databases_backed_up.length}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td>
                                            {report.directories_backed_up?.length > 0 ? (
                                                <span className="badge bg-info">
                                                    {report.directories_backed_up.length}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td>
                                            {report.media_storages_backed_up?.length > 0 ? (
                                                <span className="badge bg-info">
                                                    {report.media_storages_backed_up.length}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-primary"
                                                onClick={() => showLogs(report)}
                                            >
                                                <em className="fa fa-eye"></em> Логи
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <nav>
                    <ul className="pagination">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                Предыдущая
                            </button>
                        </li>
                        <li className="page-item active">
                            <span className="page-link">
                                Страница {currentPage} из {totalPages}
                            </span>
                        </li>
                        <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage >= totalPages}
                            >
                                Следующая
                            </button>
                        </li>
                    </ul>
                </nav>
            )}

            {showLogModal && selectedReport && (
                <Modal
                    id={`backup-report-modal-${selectedReport.id}`}
                    title={`Логи резервного копирования #${selectedReport.id}`}
                    show={showLogModal}
                    onClose={() => setShowLogModal(false)}
                >
                    <div className="backup-report-details">
                        <div className="mb-3">
                            <strong>Ресурс:</strong> {selectedReport.resource_name || <code className="text-muted">{selectedReport.key}</code>}
                        </div>
                        {selectedReport.resource_name && (
                            <div className="mb-3">
                                <strong>Ключ:</strong> <code className="text-muted small">{selectedReport.key}</code>
                            </div>
                        )}
                        <div className="mb-3">
                            <strong>Дата/Время:</strong> {formatDate(selectedReport.timestamp)}
                        </div>
                        <div className="mb-3">
                            <strong>Статус:</strong> {getStatusBadge(selectedReport.status)}
                        </div>
                        
                        {selectedReport.error_message && (
                            <div className="mb-3 alert alert-danger">
                                <strong>Ошибка:</strong> {selectedReport.error_message}
                            </div>
                        )}

                        {selectedReport.files && selectedReport.files.length > 0 && (
                            <div className="mb-3">
                                <strong>Созданные файлы:</strong>
                                <ul className="list-group mt-2">
                                    {selectedReport.files.map((file, index) => (
                                        <li key={index} className="list-group-item d-flex justify-content-between">
                                            <span>{file.filename}</span>
                                            <span>{formatFileSize(file.size)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {selectedReport.databases_backed_up && selectedReport.databases_backed_up.length > 0 && (
                            <div className="mb-3">
                                <strong>Базы данных:</strong>
                                <ul className="list-group mt-2">
                                    {selectedReport.databases_backed_up.map((db, index) => (
                                        <li key={index} className="list-group-item">{db}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {selectedReport.directories_backed_up && selectedReport.directories_backed_up.length > 0 && (
                            <div className="mb-3">
                                <strong>Директории:</strong>
                                <ul className="list-group mt-2">
                                    {selectedReport.directories_backed_up.map((dir, index) => (
                                        <li key={index} className="list-group-item">{dir}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {selectedReport.media_storages_backed_up && selectedReport.media_storages_backed_up.length > 0 && (
                            <div className="mb-3">
                                <strong>Media storage:</strong>
                                <ul className="list-group mt-2">
                                    {selectedReport.media_storages_backed_up.map((path, index) => (
                                        <li key={index} className="list-group-item">{path}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="mb-3">
                            <strong>Логи:</strong>
                            <pre className="backup-logs mt-2">
                                {selectedReport.logs || 'Нет логов'}
                            </pre>
                        </div>
                    </div>
                </Modal>
            )}
            </div>
        </div>
    );
};

export default BackupReports;

